import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";

export default function Gallery() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [caption, setCaption] = useState("");
  const [fdi, setFdi] = useState("");
  const [aiFeedback, setAiFeedback] = useState(true);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast("Please sign in to submit a wax-up.");
      navigate("/login");
      return;
    }
    if (!file) {
      toast.error("Pick an image first");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      if (fdi) fd.append("fdi_tooth", fdi);
      fd.append("request_ai_feedback", aiFeedback ? "true" : "false");
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Wax-up submitted");
      setCaption(""); setFdi(""); setFile(null); setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const featureToggle = async (id) => {
    try {
      await api.post(`/gallery/${id}/feature`);
      await load();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="gallery-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="font-mono-label text-primary">Student Studio</div>
          <h1 className="font-serif-display text-5xl md:text-6xl font-light mt-3 tracking-tighter leading-none">
            Wax-up gallery
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Share your practice wax-ups. Get precise, instant feedback from Porcelain (Claude 4.5) and learn from fellow students' work.
          </p>
        </div>
        <button
          onClick={() => user ? setShowForm(!showForm) : navigate("/login")}
          className="btn-oxblood text-sm inline-flex items-center gap-2 self-start"
          data-testid="gallery-open-submit"
        >
          <Upload className="w-4 h-4" /> {showForm ? "Close form" : "Submit your wax-up"}
        </button>
      </div>

      {showForm && user && (
        <form onSubmit={submit} className="porcelain-card p-6 mb-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gallery-form">
          <div className="md:col-span-2">
            <label className="font-mono-label text-muted-foreground block mb-2">Photo (JPEG/PNG/WebP, max 5 MB)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full bg-white border border-border px-4 py-3 text-sm"
              style={{ borderRadius: 2 }}
              data-testid="gallery-file"
            />
          </div>
          <div>
            <label className="font-mono-label text-muted-foreground block mb-2">FDI tooth (optional)</label>
            <input
              type="text"
              maxLength={4}
              placeholder="e.g., 36"
              value={fdi}
              onChange={(e) => setFdi(e.target.value)}
              className="w-full bg-white border border-border px-4 py-3 text-sm"
              style={{ borderRadius: 2 }}
              data-testid="gallery-fdi"
            />
          </div>
          <div className="flex items-end gap-3">
            <label className="inline-flex items-center gap-2 text-sm" data-testid="gallery-ai-toggle">
              <input type="checkbox" checked={aiFeedback} onChange={(e) => setAiFeedback(e.target.checked)} />
              Request AI feedback
              <Sparkles className="w-4 h-4 text-primary" />
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="font-mono-label text-muted-foreground block mb-2">Caption — describe your work and what feedback you want</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={4}
              required
              maxLength={600}
              placeholder="Practice wax-up of FDI 36. Worked on cusp placement and the Y-groove pattern — would like feedback on the mesiobuccal triangular ridge."
              className="w-full bg-white border border-border px-4 py-3 text-sm"
              style={{ borderRadius: 2 }}
              data-testid="gallery-caption"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={submitting} className="btn-oxblood text-sm disabled:opacity-50" data-testid="gallery-submit">
              {submitting ? "Uploading…" : "Submit"}
            </button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="porcelain-card p-10 text-center" data-testid="gallery-empty">
          <div className="font-mono-label text-muted-foreground">GALLERY EMPTY</div>
          <p className="mt-4 text-muted-foreground">Be the first to share a wax-up.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <div key={it.id} className="porcelain-card overflow-hidden flex flex-col" data-testid={`gallery-item-${it.id}`}>
              <div className="relative">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${it.image_url}`} alt={it.caption} className="w-full h-56 object-cover bg-secondary" loading="lazy" />
                {it.is_featured && (
                  <div className="absolute top-3 left-3 font-mono-label bg-primary text-primary-foreground px-2 py-1 flex items-center gap-1" style={{ borderRadius: 2 }}>
                    <Star className="w-3 h-3" /> FEATURED
                  </div>
                )}
                {it.fdi_tooth && (
                  <div className="absolute top-3 right-3 font-mono-label bg-background border border-border px-2 py-1" style={{ borderRadius: 2 }}>
                    FDI {it.fdi_tooth}
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="font-mono-label text-muted-foreground">{it.user_name} · {new Date(it.created_at).toLocaleDateString()}</div>
                <p className="text-sm mt-3 leading-relaxed">{it.caption}</p>
                {it.ai_feedback && (
                  <div className="mt-4 bg-secondary/50 border-l-2 border-primary p-3 text-sm leading-relaxed" data-testid={`gallery-feedback-${it.id}`}>
                    <div className="font-mono-label text-primary flex items-center gap-1 mb-2">
                      <Sparkles className="w-3 h-3" /> Porcelain · Feedback
                    </div>
                    {it.ai_feedback}
                  </div>
                )}
                {user?.is_admin && (
                  <button
                    onClick={() => featureToggle(it.id)}
                    className="mt-4 self-start font-mono-label text-xs border border-foreground px-2 py-1 hover:bg-foreground hover:text-background transition-colors"
                    style={{ borderRadius: 2 }}
                    data-testid={`gallery-feature-${it.id}`}
                  >
                    {it.is_featured ? "Unfeature" : "Feature"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
